import { VisibilityState } from '@tanstack/react-table';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query-v5';
import api from '../api';
import {
  UpsertUserPreferencesBodyDto,
  UserPreferencesResponseDto,
} from '../api-client';
import { QueryKey } from '../enums';

export const userPreferenceKeys = {
  pinnedOrganizations: 'extension-pinned-organizations',
  tableSettings: 'table-settings',
};

export enum TableName {
  Tickets = 'tickets',
  Sites = 'sites',
  Contracts = 'contracts',
  InvoiceDetails = 'invoice-details',
  Documents = 'documents',
}

export interface TableSettings
  extends Partial<
    Record<
      TableName,
      {
        visibility?: VisibilityState;
      }
    >
  > {}

export const useGetUserPreferences = (
  query?: Parameters<
    typeof api.userPreferences.userPreferencesControllerGetUserPreferences
  >[0],
  options?: UseQueryOptions<
    UserPreferencesResponseDto,
    Error,
    UserPreferencesResponseDto
  >
) => {
  return useQuery({
    ...options,
    queryKey: [QueryKey.Resource.UserPreferences],
    queryFn: async () => {
      return api.userPreferences.userPreferencesControllerGetUserPreferences(
        query
      );
    },
  });
};

interface UpsertUserPreferencesParams {
  key: string;
  query: {
    key: string;
  };
  data: UpsertUserPreferencesBodyDto;
}

export const useUpdateUserPreferences = (
  options?: UseMutationOptions<
    UpsertUserPreferencesBodyDto,
    Error,
    UpsertUserPreferencesParams
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.UserPreferences],
      });

      options?.onSuccess?.(data, variables, context);
    },

    mutationFn: ({ key, query, data }) =>
      api.userPreferences.userPreferencesControllerUpsertPinnedOrganizations(
        key,
        query,
        data
      ),
  });
};
